import { supabase } from '@/lib/supabase';

/** Сторона миниатюры. 200 px хватает, чтобы узнать блюдо в списке дневника. */
const THUMB_SIZE = 200;
const THUMB_QUALITY = 0.7;
const BUCKET = 'meal-photos';

/**
 * Превью блюда в дневнике.
 *
 * Хранится МИНИАТЮРА, а не исходный снимок, и это принципиально:
 * - 200×200 весит ~15 КБ против ~200 КБ у полного кадра (в 13 раз меньше);
 * - на таком размере случайное лицо или документ в углу кадра превращаются
 *   в несколько пикселей — узнать нельзя.
 *
 * Включается человеком в профиле, по умолчанию выключено. В политике поэтому
 * честная формулировка «сохраняем уменьшенную копию, если ты включил»,
 * а не «храним всё молча».
 */
export async function makeThumb(dataUrl: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Кадрируем по центру в квадрат: в ленте все карточки одного размера,
      // а тарелка почти всегда в середине кадра.
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - side) / 2;
      const sy = (img.naturalHeight - side) / 2;

      const canvas = document.createElement('canvas');
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return resolve(null);
      ctx.drawImage(img, sx, sy, side, side, 0, 0, THUMB_SIZE, THUMB_SIZE);
      canvas.toBlob((b) => resolve(b), 'image/jpeg', THUMB_QUALITY);
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Кладёт миниатюру в хранилище и возвращает ПУТЬ (не ссылку).
 * Корзина закрытая — ссылки выдаются подписанными и на время, чтобы снимки
 * еды не утекали по угаданному адресу.
 */
export async function uploadMealThumb(
  mealId: string,
  userId: string,
  dataUrl: string,
): Promise<string | null> {
  try {
    const blob = await makeThumb(dataUrl);
    if (!blob) return null;
    const path = `${userId}/${mealId}.jpg`;
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, blob, { contentType: 'image/jpeg', upsert: true });
    if (error) {
      console.warn('[mealThumb] upload failed:', error.message);
      return null;
    }
    return path;
  } catch (e) {
    console.warn('[mealThumb] failed:', e);
    return null;
  }
}

/**
 * Подписанные ссылки на миниатюры — пачкой за один запрос.
 * Поштучно на десяток приёмов это десяток обращений и заметная задержка списка.
 */
export async function signThumbs(paths: string[]): Promise<Record<string, string>> {
  const clean = paths.filter(Boolean);
  if (!clean.length) return {};
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(clean, 60 * 60 * 6);
  if (error || !data) return {};
  const map: Record<string, string> = {};
  data.forEach((d: any) => {
    if (d?.path && d?.signedUrl) map[d.path] = d.signedUrl;
  });
  return map;
}
